// @flow
import React from "react";
import { withGlobalContext } from "../../../common/GlobalContext";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import get from "lodash/get";
import find from "lodash/find";
import AgentSocial from "../../../agent-detail/AgentSocial";
import BrokerInfo from "../compliance/BrokerInfo";
import BrokerUrl from "../compliance/BrokerUrl";

import type {
  $SiteOffice,
  $Site,
  ComplianceItems,
  $SiteOfficeBroker,
} from "flow/types";

type $Props = {
  office: $SiteOffice,
  site: $Site,
  classes: Object,
  compliance: ComplianceItems,
  brokers?: $SiteOfficeBroker[],
};

const OfficeInfo = ({ office, site, classes, compliance, brokers }: $Props) => {
  const hiddenOffices = get(site, "uiConfig.hiddenOffices", []);
  let broker;
  if (office) {
    if (site.siteOffices.length > 1) {
      return site.siteOffices.map((siteOffice) => {
        if (!(hiddenOffices && hiddenOffices.includes(siteOffice.id))) {
          const urlFriendlyAddress: string = encodeURIComponent(
            `${siteOffice.fullStreetAddress || ""} ${siteOffice.cityStateZip}`
          );
          if (
            compliance.officeOptions &&
            compliance.officeOptions.showFooterBrokers &&
            brokers &&
            brokers.length
          ) {
            // find the appropriate broker
            broker = find(brokers, { mlsId: office.mlsOfficeId });
          } else {
            broker = {
              fullName: siteOffice.brokerName,
              url: siteOffice.brokerUrl,
              title: siteOffice.brokerTitle,
              officePhone: siteOffice.brokerOfficePhone,
              cellPhone: siteOffice.brokerCellPhone,
              officePhoneFormatted: siteOffice.brokerOfficePhoneFormatted,
              cellPhoneFormatted: siteOffice.brokerCellPhoneFormatted,
            };
          }
          return (
            <div key={siteOffice.id} className={classes.root}>
              {siteOffice.logo ? (
                <div className={classes.officeLogo}>
                  <img
                    src={siteOffice.logo}
                    alt={siteOffice.name || ""}
                    title={siteOffice.name || ""}
                  />
                </div>
              ) : null}
              {siteOffice.name ? (
                <Typography className={classes.text} type="body">
                  {siteOffice.name}
                </Typography>
              ) : null}
              {broker && <BrokerInfo broker={broker} />}
              {siteOffice.officePhone ? (
                <Typography type="body" className={classes.text}>
                  <a
                    href={`tel:${siteOffice.officePhone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.text}
                  >
                    Office: {siteOffice.officePhoneFormatted}
                  </a>
                </Typography>
              ) : null}
              {siteOffice.streetAddress1 ? (
                <Typography type="body" className={classes.text}>
                  <a
                    href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {siteOffice.streetAddress1}
                  </a>
                </Typography>
              ) : null}
              {siteOffice.streetAddress2 ? (
                <Typography type="body" className={classes.text}>
                  <a
                    href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {siteOffice.streetAddress2}
                  </a>
                </Typography>
              ) : null}
              {siteOffice.cityStateZip ? (
                <Typography type="body" className={classes.text}>
                  <a
                    href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {siteOffice.cityStateZip}
                  </a>
                </Typography>
              ) : null}
              {broker && <BrokerUrl broker={broker} />}
              {siteOffice.url && (
                <div>
                  <a
                    href={`${siteOffice.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {siteOffice.url}
                  </a>
                </div>
              )}
              {siteOffice.socialMedia ? (
                <AgentSocial
                  className={classes.social}
                  socialMedia={siteOffice.socialMedia}
                />
              ) : null}
            </div>
          );
        }
        return null;
      });
    } else {
      const urlFriendlyAddress: string = encodeURIComponent(
        `${office.fullStreetAddress || ""} ${office.cityStateZip}`
      );
      if (
        compliance.officeOptions &&
        compliance.officeOptions.showFooterBrokers &&
        brokers &&
        brokers.length
      ) {
        // find the appropriate broker
        broker = find(brokers, { mlsId: office.mlsOfficeId });
      } else {
        broker = {
          fullName: office.brokerName,
          url: office.brokerUrl,
          title: office.brokerTitle,
          officePhone: office.brokerOfficePhone,
          cellPhone: office.brokerCellPhone,
          officePhoneFormatted: office.brokerOfficePhoneFormatted,
          cellPhoneFormatted: office.brokerCellPhoneFormatted,
        };
      }
      return (
        <div className={classes.root}>
          {office.logo ? (
            <div className={classes.officeLogo}>
              <img
                src={office.logo}
                alt={office.name || ""}
                title={office.name || ""}
              />
            </div>
          ) : null}
          {office.name ? (
            <Typography className={classes.text} type="body">
              {office.name}
            </Typography>
          ) : null}
          {broker && <BrokerInfo broker={broker} />}
          {office.officePhone ? (
            <Typography type="body" className={classes.text}>
              <a
                href={`tel:${office.officePhone}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Office: {office.officePhoneFormatted}
              </a>
            </Typography>
          ) : null}
          {office.streetAddress1 ? (
            <Typography type="body" className={classes.text}>
              <a
                href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {office.streetAddress1}
              </a>
            </Typography>
          ) : null}
          {office.streetAddress2 ? (
            <Typography type="body" className={classes.text}>
              <a
                href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {office.streetAddress2}
              </a>
            </Typography>
          ) : null}
          {office.cityStateZip ? (
            <Typography type="body" className={classes.text}>
              <a
                href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {office.cityStateZip}
              </a>
            </Typography>
          ) : null}
          {broker && <BrokerUrl broker={broker} />}
          {office.url && (
            <div>
              <a
                href={`${office.url}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {office.url}
              </a>
            </div>
          )}
          {office.socialMedia ? (
            <AgentSocial
              className={classes.social}
              socialMedia={office.socialMedia}
            />
          ) : null}
        </div>
      );
    }
  }
  return null;
};

const styles = (theme) => {
  return {
    root: {
      "& p": {
        margin: "0 auto",
      },
    },
    title: {
      fontWeight: "bold",
    },
    social: {
      justifyContent: "center",
      margin: "12px auto",
      textAlign: "center",
      "& a": {
        color: `${theme.colors.primaryFontColor} !important`,
      },
    },
    officeLogo: {
      position: "relative",
      maxWidth: "225px",
      margin: "0 auto",
      "& img": {
        display: "block",
        width: "100%",
        height: "auto",
      },
    },
    text: {
      color: theme.colors.primaryFontColor,
      fontSize: "14px",
      lineHeight: "18px",
    },
  };
};

export default withGlobalContext(withStyles(styles)(OfficeInfo));
